import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { assets } from '../../data/sampleData';
import { SearchIcon, FilterIcon, UploadIcon, GridIcon, ListIcon, FileIcon, FileTextIcon, ImageIcon, EyeIcon, DownloadIcon, TrashIcon } from 'lucide-react';
type AssetType = 'Resume' | 'Project' | 'Marketing' | 'Documents' | 'All';
type DateFilter = '7days' | '30days' | 'all';
type ViewMode = 'grid' | 'list';
export const AssetLibraryPage: React.FC = () => {
  const [selectedTypes, setSelectedTypes] = useState<AssetType[]>(['Resume', 'Project', 'Marketing']);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const toggleType = (type: AssetType) => {
    if (type === 'All') {
      setSelectedTypes(['Resume', 'Project', 'Marketing', 'Documents']);
      return;
    }
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };
  const filteredAssets = assets.filter(asset => selectedTypes.includes(asset.type as AssetType));
  const getIconForType = (type: string) => {
    switch (type) {
      case 'Resume':
        return <FileTextIcon size={32} className="text-indigo-9" data-id="element-69" />;
      case 'Project':
        return <div size={32} className="text-green-9" data-id="element-70" />;
      case 'Marketing':
        return <ImageIcon size={32} className="text-blue-9" data-id="element-71" />;
      default:
        return <FileIcon size={32} className="text-gray-9" data-id="element-72" />;
    }
  };
  return <div data-id="element-73">
      <div className="flex justify-between items-center mb-8" data-id="element-74">
        <h1 className="text-2xl font-bold text-gray-12" data-id="element-75">Asset Library</h1>
        <div className="flex items-center space-x-3" data-id="element-76">
          <div className="relative" data-id="element-77">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-78">
              <SearchIcon size={16} className="text-gray-10" data-id="element-79" />
            </div>
            <input type="text" placeholder="Search assets..." className="pl-10 pr-4 py-2.5 h-10 border border-gray-7 rounded-md text-sm focus:ring-2 focus:ring-indigo-9 focus:border-transparent w-64" data-id="element-80" />
          </div>
          <div className="flex bg-gray-3 rounded-md p-1" data-id="element-81">
            <button className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-12' : 'text-gray-11'}`} onClick={() => setViewMode('grid')} data-id="element-82">
              <GridIcon size={18} data-id="element-83" />
            </button>
            <button className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-12' : 'text-gray-11'}`} onClick={() => setViewMode('list')} data-id="element-84">
              <ListIcon size={18} data-id="element-85" />
            </button>
          </div>
          <Button icon={<UploadIcon size={16} data-id="element-87" />} data-id="element-86">Upload</Button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-8" data-id="element-88">
        <div className="w-full md:w-48 lg:w-56 shrink-0" data-id="element-89">
          <div className="bg-white border border-gray-6 rounded-lg p-4" data-id="element-90">
            <div className="mb-6" data-id="element-91">
              <h3 className="text-sm font-semibold text-gray-11 mb-3" data-id="element-92">Type</h3>
              <div className="space-y-2" data-id="element-93">
                <label className="flex items-center text-sm cursor-pointer" data-id="element-94">
                  <input type="checkbox" className="rounded border-gray-7 text-indigo-9 mr-2 focus:ring-indigo-9" checked={selectedTypes.includes('Resume')} onChange={() => toggleType('Resume')} data-id="element-95" />
                  <span className="text-gray-11" data-id="element-96">Resumes (24)</span>
                </label>
                <label className="flex items-center text-sm cursor-pointer" data-id="element-97">
                  <input type="checkbox" className="rounded border-gray-7 text-indigo-9 mr-2 focus:ring-indigo-9" checked={selectedTypes.includes('Project')} onChange={() => toggleType('Project')} data-id="element-98" />
                  <span className="text-gray-11" data-id="element-99">Projects (18)</span>
                </label>
                <label className="flex items-center text-sm cursor-pointer" data-id="element-100">
                  <input type="checkbox" className="rounded border-gray-7 text-indigo-9 mr-2 focus:ring-indigo-9" checked={selectedTypes.includes('Marketing')} onChange={() => toggleType('Marketing')} data-id="element-101" />
                  <span className="text-gray-11" data-id="element-102">Marketing (12)</span>
                </label>
                <label className="flex items-center text-sm cursor-pointer" data-id="element-103">
                  <input type="checkbox" className="rounded border-gray-7 text-indigo-9 mr-2 focus:ring-indigo-9" checked={selectedTypes.includes('Documents')} onChange={() => toggleType('Documents')} data-id="element-104" />
                  <span className="text-gray-11" data-id="element-105">Documents (8)</span>
                </label>
              </div>
            </div>
            <div data-id="element-106">
              <h3 className="text-sm font-semibold text-gray-11 mb-3" data-id="element-107">
                Date Added
              </h3>
              <div className="space-y-2" data-id="element-108">
                <label className="flex items-center text-sm cursor-pointer" data-id="element-109">
                  <input type="radio" name="date" className="text-indigo-9 mr-2 focus:ring-indigo-9" checked={dateFilter === '7days'} onChange={() => setDateFilter('7days')} data-id="element-110" />
                  <span className="text-gray-11" data-id="element-111">Last 7 days</span>
                </label>
                <label className="flex items-center text-sm cursor-pointer" data-id="element-112">
                  <input type="radio" name="date" className="text-indigo-9 mr-2 focus:ring-indigo-9" checked={dateFilter === '30days'} onChange={() => setDateFilter('30days')} data-id="element-113" />
                  <span className="text-gray-11" data-id="element-114">Last 30 days</span>
                </label>
                <label className="flex items-center text-sm cursor-pointer" data-id="element-115">
                  <input type="radio" name="date" className="text-indigo-9 mr-2 focus:ring-indigo-9" checked={dateFilter === 'all'} onChange={() => setDateFilter('all')} data-id="element-116" />
                  <span className="text-gray-11" data-id="element-117">All time</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1" data-id="element-118">
          {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-119">
              {filteredAssets.map(asset => <div key={asset.id} className="bg-white border border-gray-6 rounded-lg p-4 hover:shadow-md transition-shadow group relative" data-id="element-120">
                  <div className="flex flex-col items-center" data-id="element-121">
                    <div className="mb-3" data-id="element-122">{getIconForType(asset.type)}</div>
                    <h3 className="text-sm font-medium text-gray-12 text-center mb-1" data-id="element-123">
                      {asset.name}
                    </h3>
                    <p className="text-xs text-gray-10 mb-3" data-id="element-124">{asset.owner}</p>
                    <div className="flex justify-between w-full text-xs text-gray-10" data-id="element-125">
                      <span data-id="element-126">{asset.size}</span>
                      <span data-id="element-127">{asset.dateAdded}</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gray-12 bg-opacity-0 group-hover:bg-opacity-10 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" data-id="element-128">
                    <button className="p-2 bg-white rounded-full shadow-sm text-gray-11 hover:text-indigo-9" data-id="element-129">
                      <EyeIcon size={16} data-id="element-130" />
                    </button>
                    <button className="p-2 bg-white rounded-full shadow-sm text-gray-11 hover:text-indigo-9" data-id="element-131">
                      <DownloadIcon size={16} data-id="element-132" />
                    </button>
                    <button className="p-2 bg-white rounded-full shadow-sm text-gray-11 hover:text-red-9" data-id="element-133">
                      <TrashIcon size={16} data-id="element-134" />
                    </button>
                  </div>
                </div>)}
            </div> : <div className="bg-white border border-gray-6 rounded-lg overflow-hidden" data-id="element-135">
              <table className="min-w-full divide-y divide-gray-6" data-id="element-136">
                <thead className="bg-gray-2" data-id="element-137">
                  <tr data-id="element-138">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-139">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-140">
                      Type
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-141">
                      Size
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-142">
                      Date Added
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-11 uppercase tracking-wider" data-id="element-143">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-6" data-id="element-144">
                  {filteredAssets.map(asset => <tr key={asset.id} className="hover:bg-gray-2 transition-colors" data-id="element-145">
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-146">
                        <div className="flex items-center" data-id="element-147">
                          <div className="mr-3" data-id="element-148">
                            {getIconForType(asset.type)}
                          </div>
                          <div className="text-sm font-medium text-gray-12" data-id="element-149">
                            {asset.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-150">
                        <div className="text-sm text-gray-11" data-id="element-151">{asset.type}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-152">
                        <div className="text-sm text-gray-11" data-id="element-153">{asset.size}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-154">
                        <div className="text-sm text-gray-11" data-id="element-155">
                          {asset.dateAdded}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm" data-id="element-156">
                        <button className="text-gray-11 hover:text-indigo-9 mx-1" data-id="element-157">
                          <EyeIcon size={16} data-id="element-158" />
                        </button>
                        <button className="text-gray-11 hover:text-indigo-9 mx-1" data-id="element-159">
                          <DownloadIcon size={16} data-id="element-160" />
                        </button>
                        <button className="text-gray-11 hover:text-red-9 mx-1" data-id="element-161">
                          <TrashIcon size={16} data-id="element-162" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
};